import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Edit, Save, X, Printer, Globe } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { practiceApi } from '../services/authApi';
import LoadingSpinner from '../components/LoadingSpinner';
import { useToast } from '../hooks/use-toast';
import TranslatePanel from '../components/TranslatePanel';

const ProblemDetailsPage = () => {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const { practice } = useAuth();
  const { toast } = useToast();
  
  const [problemData, setProblemData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [editData, setEditData] = useState({
    performedDate: '',
    notes: ''
  });

  // Translation state
  const [translatedContent, setTranslatedContent] = useState(null);
  const [translationLang, setTranslationLang] = useState(null);
  const [patientLanguage, setPatientLanguage] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const autoTranslateRef = useRef(false);
  
  // Content editing state
  const [isEditingContent, setIsEditingContent] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [savingContent, setSavingContent] = useState(false);

  useEffect(() => {
    loadProblemDetails();
  }, [assignmentId]);

  // Auto-translate when patient has a non-English language
  useEffect(() => {
    if (patientLanguage && patientLanguage !== 'en' && problemData && !translatedContent && !autoTranslateRef.current) {
      autoTranslateRef.current = true;
      const docId = problemData.problemId || assignmentId;
      const token = localStorage.getItem('dentalToken');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      setIsTranslating(true);
      fetch(`${BACKEND_URL}/api/translate/document`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_type: 'problem', document_id: docId, target_language: patientLanguage })
      })
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            const text = data.translation || data.translatedText;
            setTranslatedContent(text);
            setTranslationLang(data.language);
          }
        })
        .catch(() => {})
        .finally(() => setIsTranslating(false));
    }
  }, [patientLanguage, problemData]);

  const loadProblemDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await practiceApi.getProblemAssignment(assignmentId);
      const data = response.data;
      
      const transformedData = {
        id: data.id,
        problemId: data.problemId,
        problemName: data.problemName,
        patientName: data.patientName || '',
        performedDate: data.performedDate ? data.performedDate.split('T')[0] : '',
        notes: data.notes || '',
        problemContent: data.problemContent || '',
        category: data.category || 'Problems & Emergencies'
      };
      
      setProblemData(transformedData);
      
      // Set patient language for auto-translation
      if (data.patientPreferredLanguage && data.patientPreferredLanguage !== 'en') {
        setPatientLanguage(data.patientPreferredLanguage);
      }
      
      setEditData({
        performedDate: transformedData.performedDate,
        notes: transformedData.notes
      });
      
    } catch (err) {
      console.error('Load problem error:', err);
      setError(err.response?.data?.detail || 'Failed to load problem details');
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to load problem details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData({
      performedDate: problemData.performedDate,
      notes: problemData.notes
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      await practiceApi.updateProblemAssignment(assignmentId, {
        performedDate: editData.performedDate,
        notes: editData.notes
      });
      
      setProblemData(prev => ({
        ...prev,
        performedDate: editData.performedDate,
        notes: editData.notes
      }));
      
      setIsEditing(false);
      
      toast({
        title: "Success!",
        description: "Problem/Emergency details have been updated successfully.",
        variant: "default",
      });
      
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save changes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Content editing functions
  const handleEditContent = () => {
    setEditedContent(problemData?.problemContent || '');
    setIsEditingContent(true);
  };

  const saveContent = async () => {
    try {
      setSavingContent(true);
      const problemId = problemData?.problemId;
      
      if (!problemId) {
        throw new Error('Problem ID not found');
      }
      
      // Call API to customize problem content
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/practice/problems/${problemId}/customize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('dentalToken')}`
        },
        body: JSON.stringify({ content: editedContent })
      });

      if (!response.ok) {
        console.warn('⚠️ Practice customization API failed, but continuing with local update');
      }

      // Update local state
      setProblemData(prev => ({
        ...prev,
        problemContent: editedContent
      }));
      
      setIsEditingContent(false);
      
      toast({
        title: "Success!",
        description: "Problem content has been updated successfully.",
        variant: "default",
      });
      
    } catch (error) {
      console.error('Save content error:', error);
      toast({
        title: "Error",
        description: "Failed to save problem content. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSavingContent(false);
    }
  };

  const cancelContentEditing = () => {
    setEditedContent(problemData?.problemContent || '');
    setIsEditingContent(false);
  };

  const handlePrint = () => {
    if (!problemData) {
      toast({
        title: "Cannot Print",
        description: "Problem data not available for printing.",
        variant: "destructive",
      });
      return;
    }
    
    const printWindow = window.open('', '', 'height=800,width=800');
    const practiceName = practice?.name || 'Dental Practice';
    
    let practiceAddress = '';
    if (practice?.address) {
      if (typeof practice.address === 'object') {
        const addr = practice.address;
        practiceAddress = `${addr.street || ''}, ${addr.city || ''}, ${addr.state || ''} ${addr.zipCode || ''}`.trim();
      } else {
        practiceAddress = practice.address;
      }
    }
    
    const practicePhone = practice?.phone || '';
    const practiceEmail = practice?.email || '';
    const practiceLogo = practice?.branding?.logo || '';
    
    printWindow.document.write('<html><head><title>' + problemData.problemName + '</title>');
    printWindow.document.write('<style>');
    printWindow.document.write('@page { margin: 0.75in; }');
    printWindow.document.write('body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.6; margin: 0; }');
    printWindow.document.write('.header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 15px; page-break-after: avoid; break-after: avoid; }');
    printWindow.document.write('.logo { max-width: 150px; max-height: 80px; margin: 0 auto 15px; display: block; }');
    printWindow.document.write('.practice-name { font-size: 18px; font-weight: bold; margin-bottom: 5px; }');
    printWindow.document.write('.practice-info { font-size: 12px; color: #666; }');
    printWindow.document.write('.patient-info { margin: 20px 0; padding: 15px; background: #f9f9f9; border: 1px solid #ddd; }');
    printWindow.document.write('.patient-info-title { font-weight: bold; font-size: 14px; margin-bottom: 10px; }');
    printWindow.document.write('.patient-detail { margin-bottom: 5px; font-size: 12px; }');
    printWindow.document.write('h2, h3, h4 { page-break-after: avoid; break-after: avoid; page-break-inside: avoid; break-inside: avoid; orphans: 4; widows: 4; margin-top: 18px; margin-bottom: 10px; }');
    printWindow.document.write('p { orphans: 4; widows: 4; page-break-inside: avoid; break-inside: avoid; margin-bottom: 10px; line-height: 1.6; }');
    printWindow.document.write('.bullet-item { margin-left: 24px; margin-bottom: 6px; padding-left: 20px; text-indent: -20px; page-break-inside: avoid; break-inside: avoid; line-height: 1.5; }');
    printWindow.document.write('.footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; font-size: 10px; color: #666; page-break-inside: avoid; break-inside: avoid; }');
    printWindow.document.write('.footer-title { font-weight: bold; font-size: 12px; margin-bottom: 5px; }');
    printWindow.document.write('</style>');
    printWindow.document.write('</head><body>');
    
    // Header
    printWindow.document.write('<div class="header">');
    if (practiceLogo) {
      const logoSrc = practiceLogo.startsWith('data:image') ? practiceLogo : `data:image/png;base64,${practiceLogo}`;
      printWindow.document.write('<img src="' + logoSrc + '" class="logo" alt="Practice Logo" />');
    }
    printWindow.document.write('<div class="practice-name">' + practiceName + '</div>');
    if (practiceAddress) printWindow.document.write('<div class="practice-info">' + practiceAddress + '</div>');
    if (practicePhone) printWindow.document.write('<div class="practice-info">Phone: ' + practicePhone + '</div>');
    if (practiceEmail) printWindow.document.write('<div class="practice-info">Email: ' + practiceEmail + '</div>');
    printWindow.document.write('</div>');
    
    // Content
    const rawLines = (problemData.problemContent || '').split('\n');
    printWindow.document.write('<div style="margin-top: 20px; color: #374151; line-height: 1.625;">');
    
    let titleCount = 0;
    let i = 0;
    
    while (i < rawLines.length) {
      const line = rawLines[i];
      const trimmed = line.trim();
      
      if (trimmed === '') {
        printWindow.document.write('<div style="margin-bottom: 8px;"></div>');
        i++;
        continue;
      }
      
      // First 2 non-empty lines are the title
      if (titleCount < 2 && !trimmed.startsWith('This handout')) {
        printWindow.document.write('<h2 style="font-weight: bold; color: #111827; font-size: 24px; text-align: center; margin-bottom: 8px;">' + trimmed + '</h2>');
        i++;
        titleCount++;
        continue;
      }
      
      // Numbered items
      if (trimmed.match(/^\d+\.\s+/)) {
        printWindow.document.write('<h4 style="font-weight: bold; color: #111827; margin-top: 16px; margin-bottom: 8px;">' + trimmed + '</h4>');
        i++;
        continue;
      }
      
      // Section headings
      const isMainHeading = 
        trimmed.match(/^Common Problems \(.*Emergency\)/i) ||
        trimmed.match(/^When to Call the Dentist/i) ||
        trimmed.match(/^Warning Signs & When to Call/i) ||
        trimmed.match(/^What You May Notice/i);
      
      if (isMainHeading) {
        printWindow.document.write('<h3 style="font-weight: bold; color: #111827; margin-top: 24px; margin-bottom: 12px; font-size: 18px;">' + trimmed + '</h3>');
        i++;
        continue;
      }
      
      // Bullet points
      if (line.startsWith(' ') || line.startsWith('\t')) {
        const bulletText = trimmed.replace(/^[•-]\s*/, '');
        printWindow.document.write('<div class="bullet-item">• ' + bulletText + '</div>');
        i++;
        continue;
      }
      
      // Regular paragraph
      let paragraph = trimmed;
      let j = i + 1;
      while (j < rawLines.length) {
        const nextLine = rawLines[j];
        const nextTrimmed = nextLine.trim();
        
        if (nextTrimmed === '' || 
            nextTrimmed.match(/^\d+\./) || 
            nextLine.startsWith(' ') ||
            nextLine.startsWith('\t') ||
            nextTrimmed.match(/^(Common Problems|When to Call|Warning Signs|What You May)/i)) {
          break;
        }
        
        paragraph += ' ' + nextTrimmed;
        j++;
      }
      
      printWindow.document.write('<p style="margin-bottom: 8px; line-height: 1.5; color: #374151;">' + paragraph + '</p>');
      i = j;
    }
    
    printWindow.document.write('</div>');
    
    // Patient Information Box
    printWindow.document.write('<div class="patient-info">');
    printWindow.document.write('<div class="patient-info-title">Patient Information</div>');
    printWindow.document.write('<div class="patient-detail"><strong>Patient:</strong> ' + (problemData.patientName || 'N/A') + '</div>');
    printWindow.document.write('<div class="patient-detail"><strong>Date Assigned:</strong> ' + new Date(problemData.performedDate).toLocaleDateString() + '</div>');
    if (problemData.notes) {
      printWindow.document.write('<div class="patient-detail"><strong>Notes:</strong> ' + problemData.notes + '</div>');
    }
    printWindow.document.write('</div>');
    
    // Footer
    printWindow.document.write('<div class="footer">');
    printWindow.document.write('<div class="footer-title">PractCom</div>');
    printWindow.document.write('<div>© ' + new Date().getFullYear() + ' The Oncall Bot LLC. All rights reserved.</div>');
    printWindow.document.write('<div>Powered by PractCom</div>');
    printWindow.document.write('</div>');
    
    printWindow.document.write('</body></html>');
    printWindow.document.close();

    // Modern Chrome no longer reliably fires onload from a document.write'd
    // <script> tag — trigger print directly with readyState + safety net.
    const triggerPrint = () => {
      try {
        printWindow.focus();
        printWindow.print();
        setTimeout(() => { try { printWindow.close(); } catch {} }, 500);
      } catch (e) {
        console.error('Print trigger error:', e);
      }
    };
    // Single guarded trigger. 300ms delay lets Chrome parse the inline
    // <style> before print() fires; guard prevents the ghost-window flash.
    let _printed = false;
    const guardedPrint = () => { if (_printed) return; _printed = true; triggerPrint(); };
    setTimeout(guardedPrint, 300);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  if (error || !problemData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center">
            <p className="text-red-600">{error || 'Problem not found'}</p>
            <Button onClick={() => navigate('/')} className="mt-4">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center flex-1">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/')}
                className="mr-4 p-2 hover:bg-gray-100 rounded-full"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex-1">
                <Badge variant="secondary" className="mb-2">
                  {problemData.category}
                </Badge>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {problemData.problemName}
                </h1>
              </div>
            </div>
            
            <div className="flex gap-2 items-center">
              <TranslatePanel
                documentType="problem"
                documentId={problemData?.problemId || assignmentId}
                token={localStorage.getItem('dentalToken')}
                compact={true}
                autoTranslateLanguage={patientLanguage}
                onTranslating={setIsTranslating}
                onTranslated={(text, langCode, langName) => {
                  setTranslatedContent(text);
                  setTranslationLang(langName);
                }}
              />
              <Button 
                onClick={handlePrint}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Printer className="h-4 w-4" />
                {translationLang ? `Print (${translationLang})` : 'Print'}
              </Button>
              <Button
                onClick={handleEditContent}
                className="bg-green-600 hover:bg-green-700 text-white flex items-center"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        {/* Content Card - EXACTLY like library */}
        <Card className="mb-8 border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            {/* Content Editing Interface */}
            {isEditingContent ? (
              <div className="space-y-4 mb-6 p-4 border-2 border-blue-200 rounded-lg bg-blue-50">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-blue-800">Edit Problem Content</h3>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={saveContent}
                      disabled={savingContent}
                      className="flex items-center gap-2"
                    >
                      <Save className="h-4 w-4" />
                      {savingContent ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={cancelContentEditing}
                      disabled={savingContent}
                      className="flex items-center gap-2"
                    >
                      <X className="h-4 w-4" />
                      Cancel
                    </Button>
                  </div>
                </div>
                <textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  className="w-full h-64 p-4 border border-gray-300 rounded-lg resize-vertical font-mono text-sm"
                  placeholder="Enter problem content..."
                />
                <p className="text-sm text-gray-600">
                  This content will appear in the generated PDFs. Edit the raw text here.
                </p>
              </div>
            ) : null}
            
            <div className="text-gray-700 leading-relaxed">
              {isTranslating ? (
                <div className="p-8 text-center">
                  <div className="animate-pulse flex flex-col items-center gap-3">
                    <Globe className="h-8 w-8 text-blue-500 animate-spin" />
                    <p className="text-blue-700 font-medium">Translating content...</p>
                    <p className="text-gray-500 text-sm">This may take a few seconds</p>
                  </div>
                </div>
              ) : translatedContent ? (
                <div className="whitespace-pre-wrap">
                  {translatedContent.split('\n').map((line, i) => {
                    const trimmed = line.trim();
                    if (!trimmed) return <div key={i} className="mb-2"></div>;
                    if (trimmed.startsWith('# ')) return <h2 key={i} className="font-bold text-gray-900 text-2xl text-center mb-2">{trimmed.substring(2)}</h2>;
                    if (trimmed.startsWith('## ')) return <h3 key={i} className="font-bold text-gray-900 text-lg mt-4 mb-2">{trimmed.substring(3)}</h3>;
                    if (trimmed.startsWith('- ')) return <p key={i} className="ml-4 mb-1">• {trimmed.substring(2)}</p>;
                    if (trimmed.match(/^\d+\.\s/)) return <h4 key={i} className="font-bold text-gray-900 mt-4 mb-2">{trimmed}</h4>;
                    return <p key={i} className="mb-2">{trimmed}</p>;
                  })}
                </div>
              ) : (
              (() => {
                const rawLines = problemData.problemContent.split('\n');
                const elements = [];
                let titleCount = 0;
                let i = 0;
                
                while (i < rawLines.length) {
                  const line = rawLines[i];
                  const trimmedLine = line.trim();
                  
                  // Skip empty lines
                  if (trimmedLine === '') {
                    elements.push(<div key={`empty-${i}`} className="mb-2"></div>);
                    i++;
                    continue;
                  }
                  
                  // First 2 non-empty lines are the title - larger and centered
                  if (titleCount < 2 && !trimmedLine.startsWith('This handout')) {
                    elements.push(
                      <h2 key={`title-${i}`} className="font-bold text-gray-900 text-2xl text-center mb-2">
                        {trimmedLine}
                      </h2>
                    );
                    i++;
                    titleCount++;
                    continue;
                  }
                  
                  // Numbered items
                  if (trimmedLine.match(/^\d+\.\s+/)) {
                    elements.push(
                      <h4 key={`num-${i}`} className="font-bold text-gray-900 mt-4 mb-2">
                        {trimmedLine}
                      </h4>
                    );
                    i++;
                    continue;
                  }
                  
                  // Section headings
                  const isMainHeading = 
                    trimmedLine.match(/^Common Problems \(.*Emergency\)/i) ||
                    trimmedLine.match(/^When to Call the Dentist/i) ||
                    trimmedLine.match(/^Warning Signs & When to Call/i) ||
                    trimmedLine.match(/^What You May Notice/i);
                  
                  if (isMainHeading) {
                    elements.push(
                      <h3 key={`heading-${i}`} className="font-bold text-gray-900 mt-6 mb-3 text-lg">
                        {trimmedLine}
                      </h3>
                    );
                    i++;
                    continue;
                  }
                  
                  // Bullet points (indented)
                  if (line.startsWith(' ') || line.startsWith('\t')) {
                    const bulletText = trimmedLine.replace(/^[•-]\s*/, '');
                    elements.push(
                      <div key={`bullet-${i}`} className="flex items-start mb-1 ml-6">
                        <span className="text-gray-600 mr-2">•</span>
                        <span className="flex-1 text-gray-700">{bulletText}</span>
                      </div>
                    );
                    i++;
                    continue;
                  }
                  
                  // Regular paragraph - merge consecutive non-indented lines
                  let paragraph = trimmedLine;
                  let j = i + 1;
                  while (j < rawLines.length) {
                    const nextLine = rawLines[j];
                    const nextTrimmed = nextLine.trim();
                    
                    // Stop if empty, numbered, heading, or indented
                    if (nextTrimmed === '' || 
                        nextTrimmed.match(/^\d+\./) || 
                        nextLine.startsWith(' ') ||
                        nextLine.startsWith('\t') ||
                        nextTrimmed.match(/^(Common Problems|When to Call|Warning Signs|What You May)/i)) {
                      break;
                    }
                    
                    paragraph += ' ' + nextTrimmed;
                    j++;
                  }
                  
                  elements.push(
                    <p key={`para-${i}`} className="mb-2 text-gray-700">
                      {paragraph}
                    </p>
                  );
                  i = j;
                }
                
                return elements;
              })()
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Assignment Details Card - EXACT SAME AS PROCEDURES */}
        <Card className="shadow-md border-gray-200">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Patient Information */}
              <div className="bg-blue-25 rounded-lg p-4 border border-blue-100">
                <h3 className="font-bold text-gray-900 mb-4">Patient Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <span className="font-medium text-gray-700 w-20">Name:</span>
                    <span className="text-gray-900 font-semibold">{problemData.patientName}</span>
                  </div>
                </div>
              </div>

              {/* Assignment Details - WITH INLINE EDITING LIKE PROCEDURES */}
              <div className="bg-green-25 rounded-lg p-4 border border-green-100">
                <h3 className="font-bold text-gray-900 mb-4">Assignment Details</h3>
                <div className="space-y-3">
                  {/* Date Assigned - Editable */}
                  <div className="flex items-center">
                    <span className="font-medium text-gray-700 w-24">Assigned:</span>
                    {isEditing ? (
                      <Input
                        type="date"
                        value={editData.performedDate}
                        onChange={(e) => handleInputChange('performedDate', e.target.value)}
                        className="w-auto"
                        size="sm"
                      />
                    ) : (
                      <span className="text-gray-900 font-semibold">
                        {new Date(problemData.performedDate).toLocaleDateString('en-US', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    )}
                  </div>
                  
                  {/* Notes - Editable */}
                  <div>
                    <span className="font-medium text-gray-700 block mb-2">Notes:</span>
                    {isEditing ? (
                      <Textarea
                        value={editData.notes}
                        onChange={(e) => handleInputChange('notes', e.target.value)}
                        rows={3}
                        placeholder="Add any notes..."
                        className="w-full"
                      />
                    ) : (
                      <p className="text-gray-900">{problemData.notes || 'No notes added'}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProblemDetailsPage;
